import { clientHref } from './operation-results';

import type { ReleaseClients } from '../schemas/release-component';

/**
 * The release page's clients section as plain data: who the release is on for
 * now, complete first. Names come from the clients list, which is a separate
 * read and may be missing; the tenant id stands in.
 */

export interface ReleaseClientRow {
    readonly tenantId: string;
    readonly name: string;
    /** The client's Flags tab. */
    readonly href: string;
    readonly complete: boolean;
    readonly coverageLabel: 'Complete' | 'Partial';
    readonly detail: string;
}

export type ReleaseClientsView =
    | { readonly kind: 'all'; readonly label: 'All clients'; readonly description: string }
    | {
          readonly kind: 'list';
          readonly rows: readonly ReleaseClientRow[];
          /** Set when there is nothing to list. */
          readonly empty?: string;
          /** Set when the list is a lower bound. */
          readonly note?: string;
      };

export function releaseClientsView(
    data: ReleaseClients,
    clients: readonly { readonly tenantId: string; readonly displayName: string }[] | undefined,
    /** The release's active (non-archived) flags: the denominator of each row. */
    flagCount: number,
): ReleaseClientsView {
    if (data.appliedToAll) {
        return {
            kind: 'all',
            label: 'All clients',
            description:
                'Every flag in this release is on by default, so it applies to every client that has not turned a flag off.',
        };
    }

    const names = new Map((clients ?? []).map((client) => [client.tenantId, client.displayName]));
    const flags = flagCount === 1 ? 'flag' : 'flags';
    const rows = data.clients
        .map((client): ReleaseClientRow => {
            const complete = client.coverage === 'complete';
            return {
                tenantId: client.tenantId,
                name: names.get(client.tenantId) ?? client.tenantId,
                href: clientHref(client.tenantId),
                complete,
                coverageLabel: complete ? 'Complete' : 'Partial',
                detail: `${String(client.enabledFlagKeys.length)} of ${String(flagCount)} ${flags} on`,
            };
        })
        .sort(
            (a, b) =>
                Number(b.complete) - Number(a.complete) ||
                a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
        );

    return {
        kind: 'list',
        rows,
        ...(rows.length === 0 ? { empty: 'No clients found' } : {}),
        ...(data.lowerBound
            ? {
                  note: 'Read from client overrides. Clients whose overrides predate the override index may be missing.',
              }
            : {}),
    };
}
